import type { i18n } from 'next-i18next/pages'
import { isNonEmptyString } from 'shared/utils/is-non-empty-string'

const collectNamespaces = (i18nInstance: NonNullable<typeof i18n>): string[] => {
  const namespaces = new Set<string>()

  const { options, store, reportNamespaces } = i18nInstance

  const defaultNamespaces = options?.ns
  if (Array.isArray(defaultNamespaces)) {
    for (const namespace of defaultNamespaces) {
      if (isNonEmptyString(namespace)) namespaces.add(namespace)
    }
  } else if (isNonEmptyString(defaultNamespaces)) {
    namespaces.add(defaultNamespaces)
  }

  if (store?.data) {
    for (const localeStore of Object.values(store.data)) {
      if (!localeStore) continue

      for (const namespace of Object.keys(localeStore)) namespaces.add(namespace)
    }
  }

  const usedNamespaces = reportNamespaces?.getUsedNamespaces?.()
  if (usedNamespaces) {
    for (const namespace of usedNamespaces.filter(Boolean)) namespaces.add(namespace)
  }

  return [...namespaces].filter(Boolean)
}

const loadNamespaceModule = async (locale: string, namespace: string): Promise<any> => {
  try {
    const moduleTranslation = await import(
      /* webpackChunkName: "i18n-[request]" */
      /* webpackMode: "lazy" */
      `../../../public/locales/${locale}/${namespace}.json`
    )
    return moduleTranslation.default ?? moduleTranslation
  } catch (error) {
    const details = error instanceof Error ? error.message : 'Unknown error'
    throw new Error(`Failed to load locale namespace ${locale}/${namespace}: ${details}`, {
      cause: error,
    })
  }
}

export const loadLocaleBundles = async (
  i18nInstance: typeof i18n | null | undefined,
  locale: string | undefined,
): Promise<void> => {
  if (!i18nInstance || !locale) return

  const namespaces = collectNamespaces(i18nInstance)
  if (namespaces.length === 0) return

  for (const namespace of namespaces) {
    if (!namespace) continue
    if (i18nInstance.hasResourceBundle(locale, namespace)) continue

    const resources = await loadNamespaceModule(locale, namespace)
    i18nInstance.addResourceBundle(locale, namespace, resources, true, true)
  }
}
